import { isRecord } from '@myre/common-primitives';
import {
  SimpleCellType,
  type CellSimpleCell,
  type SimpleExport,
} from '@myre/common-sheet';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { SheetComponent } from '@myre/ui-primitive-components';
import { TooltipDirective } from '@myre/ui-primitive-tooltip-directive';

import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, signal } from '@angular/core';
import { isNil } from 'lodash-es';

import { WORDINGS } from '../../../myre-wordings-loader';

import {
  type Citation,
  type CitationEvidence,
  type CitationStep,
} from '../../interfaces/chat.interfaces';

/** One rendered table (top-level array or array property of an object). */
interface TableView {
  key: string;
  label: string;
  sheet: SimpleExport;
  previewSheet: SimpleExport;
  rowCount: number;
  totalItems?: number;
}

/** Deterministic human rendering of one tool result. */
interface KvEntry {
  key: string;
  value: string;
  /** Object/array values render as a small pre block, not inline text. */
  isJson: boolean;
}

type EvidenceView =
  | { kind: 'table'; table: TableView }
  | {
      kind: 'composite';
      entries: KvEntry[];
      tables: TableView[];
    }
  | { kind: 'raw'; text: string };

interface EvidenceItemView {
  tool: string;
  label: string;
  /** True when the label is the raw args fallback (no per-tool describer). */
  labelIsRaw: boolean;
  technicalJson?: string;
  view: EvidenceView;
}

interface StepView {
  step_index: number;
  worker_name: string;
  items: EvidenceItemView[];
}

const MAX_TABLE_COLUMNS = 8;
const PREVIEW_ROWS = 5;

/**
 * Result fields that are tool plumbing, not data the user asked for: request
 * echoes and underscore-prefixed internals. Matched on the normalized key
 * (case/separator-insensitive) so `queryContext`/`query_context` are caught
 * too. They move to the technical-details tooltip instead of the visible
 * proof.
 */
const HIDDEN_KEY_NAMES = new Set([
  'query',
  'context',
  'querycontext',
  'hint',
  'meta',
]);

function isInternalKey(key: string): boolean {
  if (key.startsWith('_')) {
    return true;
  }

  const normalized = key.replace(/[_-]/g, '').toLowerCase();

  return HIDDEN_KEY_NAMES.has(normalized);
}

/** `remainingDues` / `invoice_number` → `Remaining Dues` / `Invoice Number`. */
function titleCase(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();

  return spaced
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function cellText(value: unknown): string {
  if (isNil(value)) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function toCell(value: unknown): CellSimpleCell {
  if (typeof value === 'number') {
    return { value, type: SimpleCellType.Amount };
  }

  return { type: SimpleCellType.String, value: cellText(value) };
}

function buildTable(params: {
  key: string;
  records: ReadonlyArray<Record<string, unknown>>;
  totalItems: number | undefined;
}): TableView {
  const { key, records, totalItems } = params;
  const columns: string[] = [];

  for (const row of records) {
    for (const column of Object.keys(row)) {
      if (
        !isInternalKey(column) &&
        !columns.includes(column) &&
        columns.length < MAX_TABLE_COLUMNS
      ) {
        columns.push(column);
      }
    }
  }

  const headers = columns.map(column => ({ label: titleCase(column) }));

  return {
    key,
    totalItems,
    label: key ? titleCase(key) : '',
    sheet: { headers, rows: records.map(row => toSheetRow(row, columns)) },
    previewSheet: {
      headers,
      rows: records.slice(0, PREVIEW_ROWS).map(row => toSheetRow(row, columns)),
    },
    rowCount: records.length,
  };
}

function toSheetRow(row: Record<string, unknown>, columns: string[]) {
  return { cells: columns.map(column => toCell(row[column])) };
}

/**
 * Deterministic "proof formatter": JSON arrays of records become a real
 * table, plain objects a key/value list plus nested tables, anything else
 * stays raw text. Internal fields (request echoes, `_`-prefixed) are pulled
 * out into a technical-details JSON shown only in a tooltip.
 */
function toKvEntry(key: string, value: unknown): KvEntry {
  if (typeof value === 'object' && value !== null) {
    return {
      key: titleCase(key),
      value: JSON.stringify(value, null, 2),
      isJson: true,
    };
  }

  return { key: titleCase(key), value: cellText(value), isJson: false };
}

function toEvidenceItemView(evidence: CitationEvidence): EvidenceItemView {
  const base = {
    tool: evidence.tool,
    label: evidence.call_label ?? evidence.args_summary,
    labelIsRaw: evidence.call_label === undefined,
  };

  let parsed: unknown;

  try {
    parsed = JSON.parse(evidence.result_excerpt);
  } catch {
    return { ...base, view: { kind: 'raw', text: evidence.result_excerpt } };
  }

  if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(isRecord)) {
    return {
      ...base,
      view: {
        kind: 'table',
        table: buildTable({
          key: '',
          records: parsed,
          totalItems: evidence.total_items,
        }),
      },
    };
  }

  if (isRecord(parsed)) {
    const entries: KvEntry[] = [];
    const tables: TableView[] = [];
    const technical: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(parsed)) {
      if (isInternalKey(key)) {
        technical[key] = value;
      } else if (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every(isRecord)
      ) {
        tables.push(buildTable({ key, records: value, totalItems: undefined }));
      } else {
        entries.push(toKvEntry(key, value));
      }
    }

    if (tables.length > 0 && evidence.total_items !== undefined) {
      const largest = tables.reduce((a, b) =>
        b.rowCount > a.rowCount ? b : a,
      );
      largest.totalItems = evidence.total_items;
    }

    return {
      ...base,
      ...(Object.keys(technical).length > 0
        ? { technicalJson: JSON.stringify(technical, null, 2) }
        : {}),
      view: { entries, tables, kind: 'composite' },
    };
  }

  return { ...base, view: { kind: 'raw', text: evidence.result_excerpt } };
}

function toStepView(step: CitationStep): StepView {
  const items = (step.evidence ?? []).map(evidence =>
    toEvidenceItemView(evidence),
  );

  if (items.length === 0) {
    items.push({
      tool: step.worker_name,
      label: '',
      labelIsRaw: false,
      view: { kind: 'raw', text: step.excerpt },
    });
  }

  return {
    items,
    step_index: step.step_index,
    worker_name: step.worker_name,
  };
}

/**
 * Modal body showing the full source behind a citation: per step, the worker
 * name and the raw tool results rendered for humans — real tables for tabular
 * data (previewed, expandable), key/value lists for objects, raw text as a
 * last resort. The request itself is shown via the tool's call_label when the
 * backend provides one; tool plumbing fields live in a tooltip.
 */
@Component({
  selector: 'app-citation-detail',
  imports: [NgTemplateOutlet, SheetComponent, TooltipDirective],
  templateUrl: './citation-detail.component.html',
  styleUrl: './citation-detail.component.scss',
})
export class CitationDetailComponent {
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  public readonly citation = input.required<Citation>();

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  protected readonly _steps = computed<StepView[]>(() =>
    this.citation().steps.map(step => toStepView(step)),
  );

  private readonly _expandedTables = signal<ReadonlySet<string>>(new Set());

  protected _isExpanded(step: StepView, table: TableView): boolean {
    return this._expandedTables().has(this._tableId(step, table));
  }

  protected _toggleTable(step: StepView, table: TableView): void {
    const id = this._tableId(step, table);
    const next = new Set(this._expandedTables());

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    this._expandedTables.set(next);
  }

  protected _isExpandable(table: TableView): boolean {
    return table.rowCount > PREVIEW_ROWS;
  }

  /** True when the shipped excerpt holds fewer rows than the real result. */
  protected _isCapped(table: TableView): boolean {
    return (table.totalItems ?? 0) > table.rowCount;
  }

  protected _hasFooter(table: TableView): boolean {
    return (
      this._isExpandable(table) || (table.totalItems ?? 0) > table.rowCount
    );
  }

  protected _shownRows(step: StepView, table: TableView): number {
    if (this._isExpanded(step, table)) {
      return table.rowCount;
    }

    return Math.min(PREVIEW_ROWS, table.rowCount);
  }

  private _tableId(step: StepView, table: TableView): string {
    return `${step.step_index}:${table.key}`;
  }
}
